import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { insertCorFitaSchema, updateCorFitaSchema, type InsertCorFita, type UpdateCorFita, type CorFita } from "@shared/schema";
import { Plus, Palette, X, Pencil, Trash2, ChevronUp, ChevronDown } from "lucide-react";

import { PageHeader, ALVO_TOQUE, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageToolbar, PageSearch } from "@/components/layout/page-toolbar";
import { AdminLista, AdminListaItem, AdminTabela } from "@/components/admin/admin-lista";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell, DataTableNum,
} from "@/components/data/data-table";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
const coresPreDefinidas = [
  { nome: "Vermelho", hex: "#EF4444" },
  { nome: "Rosa", hex: "#EC4899" },
  { nome: "Laranja", hex: "#F97316" },
  { nome: "Amarelo", hex: "#EAB308" },
  { nome: "Verde", hex: "#22C55E" },
  { nome: "Azul", hex: "#3B82F6" },
  { nome: "Roxo", hex: "#A855F7" },
  { nome: "Branco", hex: "#FFFFFF" },
  { nome: "Preto", hex: "#000000" },
  { nome: "Marrom", hex: "#92400E" },
];

const COLUNAS_CORES = ["7rem", null, "8rem", "16rem", "8rem", "6rem"];

// ⚠️ Constante de MÓDULO (dep de useMemo dentro do hook).
const PADRAO_BUSCA = { q: "" };

export default function CoresPage() {
  const { estado, definir } = useEstadoNaUrl(PADRAO_BUSCA);
  const busca = estado.q;
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCor, setEditingCor] = useState<CorFita | null>(null);
  const [aliasInput, setAliasInput] = useState("");
  const [selectedColor, setSelectedColor] = useState("#EF4444");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [corToDelete, setCorToDelete] = useState<CorFita | null>(null);

  const { data: cores, isLoading } = useQuery<CorFita[]>({
    queryKey: ["/api/cores"],
  });

  const form = useForm<InsertCorFita | UpdateCorFita>({
    resolver: zodResolver(editingCor ? updateCorFitaSchema : insertCorFitaSchema),
    defaultValues: {
      nome: "",
      hexColor: "#EF4444",
      aliases: [],
      ativo: true,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: InsertCorFita) => {
      const response = await apiRequest("POST", "/api/cores", data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cores"] });
      toast({
        title: "Cor cadastrada!",
        description: "A cor foi adicionada com sucesso.",
      });
      handleCloseDialog();
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao cadastrar cor",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateCorFita }) => {
      const response = await apiRequest("PATCH", `/api/cores/${id}`, data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cores"] });
      toast({
        title: "Cor atualizada!",
        description: "A cor foi atualizada com sucesso.",
      });
      handleCloseDialog();
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar cor",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("DELETE", `/api/cores/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cores"] });
      toast({
        title: "Cor excluída!",
        description: "A cor foi removida com sucesso.",
      });
      setDeleteDialogOpen(false);
      setCorToDelete(null);
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao excluir cor",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const swapOrdemMutation = useMutation({
    mutationFn: async ({ id, targetId }: { id: string; targetId: string }) => {
      return await apiRequest("PATCH", `/api/cores/${id}/ordem`, { targetId });
    },
    onMutate: async ({ id, targetId }) => {
      // Cancel any outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ["/api/cores"] });
      
      // Snapshot the previous value
      const previousCores = queryClient.getQueryData<CorFita[]>(["/api/cores"]);
      
      // Optimistically update the cache by swapping ordem values
      if (previousCores) {
        const newCores = [...previousCores];
        const cor1Index = newCores.findIndex(c => c.id === id);
        const cor2Index = newCores.findIndex(c => c.id === targetId);
        
        if (cor1Index !== -1 && cor2Index !== -1) {
          // Swap ordem values
          const tempOrdem = newCores[cor1Index].ordem;
          newCores[cor1Index] = { ...newCores[cor1Index], ordem: newCores[cor2Index].ordem };
          newCores[cor2Index] = { ...newCores[cor2Index], ordem: tempOrdem };
          
          // Re-sort to reflect new order (ativo DESC, ordem ASC, nome ASC)
          newCores.sort((a, b) => {
            if (a.ativo !== b.ativo) return b.ativo ? 1 : -1;
            if (a.ordem !== b.ordem) return a.ordem - b.ordem;
            return a.nome.localeCompare(b.nome);
          });
          
          queryClient.setQueryData(["/api/cores"], newCores);
        }
      }
      
      return { previousCores };
    },
    onError: (error: any, variables, context) => {
      // Rollback to previous state on error
      if (context?.previousCores) {
        queryClient.setQueryData(["/api/cores"], context.previousCores);
      }
      toast({
        title: "Erro ao reordenar",
        description: error.message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Always refetch to ensure consistency with server
      queryClient.invalidateQueries({ queryKey: ["/api/cores"] });
    },
  });

  // Get sorted colors - backend already returns in order (ativo DESC, ordem ASC, nome ASC)
  const sortedCores = cores || [];
  // Busca NO CLIENTE, sobre a lista já carregada. `activeCores` continua saindo
  // da lista INTEIRA: a posição no ciclo é do ciclo, não do que está filtrado.
  const visiveis = sortedCores.filter(c => combina(busca, c.nome, ((c.aliases as string[] | null) ?? []).join(" ")));
  
  // Get only active cores for navigation
  const activeCores = sortedCores.filter(c => c.ativo);

  const handleMoveUp = (cor: CorFita) => {
    console.log("handleMoveUp chamado para:", cor.nome);
    const currentIndex = activeCores.findIndex(c => c.id === cor.id);
    console.log("currentIndex:", currentIndex, "activeCores.length:", activeCores.length);
    if (currentIndex > 0) {
      const targetCor = activeCores[currentIndex - 1];
      console.log("Movendo", cor.nome, "para cima, trocando com", targetCor.nome);
      toast({
        title: "Movendo cor...",
        description: `Movendo ${cor.nome} para cima`,
      });
      swapOrdemMutation.mutate({ id: cor.id, targetId: targetCor.id });
    } else {
      console.log("Não pode mover para cima - já é primeiro ou não encontrado");
    }
  };

  const handleMoveDown = (cor: CorFita) => {
    console.log("handleMoveDown chamado para:", cor.nome);
    const currentIndex = activeCores.findIndex(c => c.id === cor.id);
    console.log("currentIndex:", currentIndex, "activeCores.length:", activeCores.length);
    if (currentIndex < activeCores.length - 1) {
      const targetCor = activeCores[currentIndex + 1];
      console.log("Movendo", cor.nome, "para baixo, trocando com", targetCor.nome);
      toast({
        title: "Movendo cor...",
        description: `Movendo ${cor.nome} para baixo`,
      });
      swapOrdemMutation.mutate({ id: cor.id, targetId: targetCor.id });
    } else {
      console.log("Não pode mover para baixo - já é último ou não encontrado");
    }
  };

  const onSubmit = (data: InsertCorFita | UpdateCorFita) => {
    if (editingCor) {
      updateMutation.mutate({ id: editingCor.id, data: data as UpdateCorFita });
    } else {
      createMutation.mutate(data as InsertCorFita);
    }
  };

  const handleAddAlias = () => {
    if (aliasInput.trim()) {
      const currentAliases = form.getValues("aliases") || [];
      if (!currentAliases.includes(aliasInput.trim())) {
        form.setValue("aliases", [...currentAliases, aliasInput.trim()]);
        setAliasInput("");
      }
    }
  };

  const handleRemoveAlias = (alias: string) => {
    const currentAliases = form.getValues("aliases") || [];
    form.setValue("aliases", currentAliases.filter(a => a !== alias));
  };

  const handleSelectPreDefinedColor = (hex: string, nome: string) => {
    setSelectedColor(hex);
    form.setValue("hexColor", hex);
    if (!form.getValues("nome") && !editingCor) {
      form.setValue("nome", nome);
    }
  };

  const handleEdit = (cor: CorFita) => {
    setEditingCor(cor);
    setSelectedColor(cor.hexColor);
    form.reset({
      nome: cor.nome,
      hexColor: cor.hexColor,
      aliases: (cor.aliases as string[]) || [],
      ativo: cor.ativo,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCor(null);
    form.reset({
      nome: "",
      hexColor: "#EF4444",
      aliases: [],
      ativo: true,
    });
    setAliasInput("");
    setSelectedColor("#EF4444");
  };

  const handleOpenDeleteDialog = (cor: CorFita) => {
    setCorToDelete(cor);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (corToDelete) {
      deleteMutation.mutate(corToDelete.id);
    }
  };

  return (
    <div className="space-y-4 md:space-y-8">
      <PageHeader
        titulo="Cores de fitas"
        descricao="Marcação de mangarás e ciclos"
        acao={
          <Button onClick={() => setDialogOpen(true)} className={ALVO_TOQUE_BOTAO} data-testid="button-new-cor">
            <Plus className="h-4 w-4 mr-2" />
            Nova cor
          </Button>
        }
      />

      <PageToolbar
        fim={<PageSearch valor={busca} onChange={v => definir({ q: v })} placeholder="Nome" testId="input-busca-cores" />}
      >
        <span />
      </PageToolbar>

      {isLoading ? (
        <PageSkeleton variante="tabela" linhas={6} />
      ) : !cores || cores.length === 0 ? (
        <EmptyState
          icone={Palette}
          titulo="Nenhuma cor cadastrada ainda"
          descricao="A fita é o que liga o mangará à colheita — o ciclo de cores define em que ordem elas se repetem no ano."
          acao={
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />Nova cor
            </Button>
          }
          testId="empty-cores"
        />
      ) : visiveis.length === 0 ? (
        <NadaEncontrado termo={busca} />
      ) : (
        <>
          {/* Tabela, não cards: a ORDEM é o dado (o ciclo de fitas se repete na
              sequência cadastrada), e sequência se lê numa coluna, não numa
              grade que quebra em 2 ou 4 conforme a largura da tela. */}
          <AdminLista>
            {visiveis.map(cor => {
              const activeIndex = activeCores.findIndex(c => c.id === cor.id);
              return (
                <AdminListaItem
                  key={cor.id}
                  testId={`cor-card-${cor.id}`}
                  atenuado={!cor.ativo}
                  identidade={
                    <span className="flex items-center gap-2">
                      {/* Cor de fita é DADO, vem de `cores.hexColor`: `style`, nunca classe. */}
                      <span
                        className="h-3.5 w-3.5 rounded-full border border-black/20 shrink-0"
                        style={{ backgroundColor: cor.hexColor }}
                      />
                      <span className="truncate">{cor.nome}</span>
                    </span>
                  }
                  meta={
                    <>
                      {cor.ativo
                        ? <span className="font-mono">#{activeIndex + 1} do ciclo</span>
                        : <StatusBadge tom="neutro">Inativo</StatusBadge>}
                      <span className="font-mono text-[11px]">{cor.hexColor}</span>
                      {cor.aliases && (cor.aliases as string[]).length > 0 && (
                        <span className="truncate">{(cor.aliases as string[]).join(", ")}</span>
                      )}
                    </>
                  }
                  acoes={
                    <>
                      <Button
                        size="icon" variant="ghost" className={ALVO_TOQUE}
                        aria-label={`Editar ${cor.nome}`}
                        onClick={() => handleEdit(cor)}
                        data-testid={`button-edit-${cor.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon" variant="ghost" className={`${ALVO_TOQUE} text-destructive hover:text-destructive`}
                        aria-label={`Excluir ${cor.nome}`}
                        onClick={() => handleOpenDeleteDialog(cor)}
                        data-testid={`button-delete-${cor.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  }
                />
              );
            })}
          </AdminLista>

          <AdminTabela>
            <DataTable colunas={COLUNAS_CORES}>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableHead num>Ordem</DataTableHead>
                  <DataTableHead>Cor</DataTableHead>
                  <DataTableHead>Código</DataTableHead>
                  <DataTableHead>Aliases</DataTableHead>
                  <DataTableHead>Situação</DataTableHead>
                  <DataTableHead />
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                {visiveis.map(cor => {
                  const activeIndex = activeCores.findIndex(c => c.id === cor.id);
                  const isFirst = activeIndex === 0;
                  const isLast = activeIndex === activeCores.length - 1;
                  return (
                    <DataTableRow key={cor.id} data-testid={`cor-row-${cor.id}`}>
                      {/* Subir/descer viram coluna de ação, ao lado da posição que
                          eles mudam — o `PATCH /ordem` é o mesmo de antes. */}
                      <DataTableNum className={!cor.ativo ? "opacity-50" : ""}>
                        {cor.ativo ? (
                          <span className="inline-flex items-center gap-1">
                            <span>{activeIndex + 1}</span>
                            <span className="flex flex-col">
                              <Button
                                size="icon" variant="ghost" className="h-4 w-5"
                                onClick={e => { e.preventDefault(); e.stopPropagation(); handleMoveUp(cor); }}
                                disabled={isFirst || swapOrdemMutation.isPending}
                                aria-label={`Subir ${cor.nome}`}
                                data-testid={`button-move-up-${cor.id}`}
                              >
                                <ChevronUp className="h-3 w-3" />
                              </Button>
                              <Button
                                size="icon" variant="ghost" className="h-4 w-5"
                                onClick={e => { e.preventDefault(); e.stopPropagation(); handleMoveDown(cor); }}
                                disabled={isLast || swapOrdemMutation.isPending}
                                aria-label={`Descer ${cor.nome}`}
                                data-testid={`button-move-down-${cor.id}`}
                              >
                                <ChevronDown className="h-3 w-3" />
                              </Button>
                            </span>
                          </span>
                        ) : "—"}
                      </DataTableNum>
                      <DataTableCell destaque className={!cor.ativo ? "opacity-50" : ""}>
                        <span className="flex items-center gap-2 min-w-0">
                          <span
                            className="h-4 w-4 rounded-full border border-black/20 shrink-0"
                            style={{ backgroundColor: cor.hexColor }}
                          />
                          <span className="truncate">{cor.nome}</span>
                        </span>
                      </DataTableCell>
                      <DataTableCell className={`font-mono text-xs text-muted-foreground ${!cor.ativo ? "opacity-50" : ""}`}>
                        {cor.hexColor}
                      </DataTableCell>
                      <DataTableCell className={!cor.ativo ? "opacity-50" : ""}>
                        {cor.aliases && (cor.aliases as string[]).length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {(cor.aliases as string[]).map(alias => (
                              <Badge key={alias} variant="outline" className="text-xs font-normal">{alias}</Badge>
                            ))}
                          </div>
                        ) : <span className="text-muted-foreground">—</span>}
                      </DataTableCell>
                      <DataTableCell>
                        {cor.ativo
                          ? <StatusBadge tom="sucesso" ponto>Ativo</StatusBadge>
                          : <StatusBadge tom="neutro">Inativo</StatusBadge>}
                      </DataTableCell>
                      <DataTableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="icon" variant="ghost"
                            onClick={() => handleEdit(cor)}
                            data-testid={`button-edit-desktop-${cor.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon" variant="ghost" className="text-destructive hover:text-destructive"
                            onClick={() => handleOpenDeleteDialog(cor)}
                            data-testid={`button-delete-desktop-${cor.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </DataTableCell>
                    </DataTableRow>
                  );
                })}
              </DataTableBody>
            </DataTable>
          </AdminTabela>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => open ? setDialogOpen(true) : handleCloseDialog()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl md:text-2xl font-semibold">
              {editingCor ? "Editar Cor de Fita" : "Nova Cor de Fita"}
            </DialogTitle>
            <DialogDescription>
              {editingCor 
                ? "Atualize as informações da cor de fita."
                : "Cadastre uma nova cor para marcar os mangarás e ciclos de produção."
              }
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Color Picker Grid */}
            <div className="space-y-3">
              <Label>Selecione uma cor pré-definida</Label>
              <div className="grid grid-cols-5 gap-3">
                {coresPreDefinidas.map((cor) => (
                  <button
                    key={cor.hex}
                    type="button"
                    onClick={() => handleSelectPreDefinedColor(cor.hex, cor.nome)}
                    className={`aspect-square rounded-lg border-2 transition-all hover-elevate ${
                      selectedColor === cor.hex
                        ? "ring-2 ring-primary ring-offset-2 scale-105"
                        : "border-border"
                    }`}
                    style={{ backgroundColor: cor.hex }}
                    data-testid={`color-preset-${cor.nome.toLowerCase()}`}
                  >
                    <span className="sr-only">{cor.nome}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome da Cor *</Label>
                <Input
                  id="nome"
                  placeholder="Ex: Vermelho, Rosa Claro..."
                  data-testid="input-nome"
                  {...form.register("nome")}
                />
                {form.formState.errors.nome && (
                  <p className="text-xs text-destructive">{form.formState.errors.nome.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="hexColor">Código Hexadecimal *</Label>
                <div className="flex gap-2">
                  <Input
                    id="hexColor"
                    placeholder="#FF0000"
                    className="font-mono uppercase"
                    value={selectedColor}
                    onChange={(e) => {
                      setSelectedColor(e.target.value);
                      form.setValue("hexColor", e.target.value);
                    }}
                    data-testid="input-hex"
                  />
                  <input
                    type="color"
                    value={selectedColor}
                    onChange={(e) => {
                      setSelectedColor(e.target.value);
                      form.setValue("hexColor", e.target.value.toUpperCase());
                    }}
                    className="w-12 h-10 rounded border cursor-pointer"
                  />
                </div>
                {form.formState.errors.hexColor && (
                  <p className="text-xs text-destructive">{form.formState.errors.hexColor.message}</p>
                )}
              </div>
            </div>

            {/* Aliases */}
            <div className="space-y-3">
              <Label>Aliases (nomes alternativos)</Label>
              <p className="text-xs text-muted-foreground">
                Adicione variações do nome (ex: "rosa" pode ter aliases "pink", "rosa claro")
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder="Digite um alias e pressione Enter"
                  value={aliasInput}
                  onChange={(e) => setAliasInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddAlias();
                    }
                  }}
                  data-testid="input-alias"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleAddAlias}
                  data-testid="button-add-alias"
                >
                  Adicionar
                </Button>
              </div>
              {form.watch("aliases") && (form.watch("aliases") || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {(form.watch("aliases") || []).map((alias) => (
                    <Badge key={alias} variant="secondary" className="gap-1">
                      {alias}
                      <button
                        type="button"
                        onClick={() => handleRemoveAlias(alias)}
                        className="hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Status Ativo/Inativo - só mostra na edição */}
            {editingCor && (
              <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
                <div className="space-y-0.5">
                  <Label htmlFor="ativo" className="text-base font-medium">Status da Cor</Label>
                  <p className="text-sm text-muted-foreground">
                    {form.watch("ativo") 
                      ? "Cor disponível para novos registros" 
                      : "Cor bloqueada para novos mangarás e colheitas"}
                  </p>
                </div>
                <Switch
                  id="ativo"
                  checked={form.watch("ativo") ?? true}
                  onCheckedChange={(checked) => form.setValue("ativo", checked)}
                  data-testid="switch-ativo"
                />
              </div>
            )}

            {/* Preview */}
            <div className="space-y-2">
              <Label>Preview</Label>
              <div
                className="h-20 w-full rounded-lg border-2"
                style={{ backgroundColor: selectedColor }}
              />
            </div>

            <DialogFooter className="flex-col-reverse md:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                data-testid="button-cancel"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                data-testid="button-submit"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? "Salvando..."
                  : editingCor
                  ? "Atualizar Cor"
                  : "Cadastrar Cor"
                }
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a cor <strong>{corToDelete?.nome}</strong>?
              Esta ação não pode ser desfeita.
              {corToDelete && (
                <div className="mt-3 p-3 rounded-lg bg-muted">
                  <div className="flex items-center gap-2">
                    <div 
                      className="h-6 w-6 rounded border"
                      style={{ backgroundColor: corToDelete.hexColor }}
                    />
                    <span className="text-sm font-medium">{corToDelete.nome}</span>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
